import { Check, ChevronDown, Copy, Stethoscope, Trash2, TriangleAlert } from "lucide-react";
import * as React from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Switch } from "@/components/ui/switch";
import {
  clearLogEntries,
  formatLogForSharing,
  getLogEntries,
  isRecordingResponses,
  recordLogEntry,
  setRecordingResponses,
  safeJson,
  subscribeToLog,
  type LogEntry,
} from "@/lib/api/request-log";
import { cn } from "@/lib/utils";

/** Subscribes to the shared failure log. */
export function useDiagnostics() {
  const entries = React.useSyncExternalStore(subscribeToLog, getLogEntries, getLogEntries);
  const recording = React.useSyncExternalStore(
    subscribeToLog,
    isRecordingResponses,
    isRecordingResponses,
  );
  return {
    entries,
    // Recorded responses aren't problems, so they don't inflate the badge.
    count: entries.filter((entry) => entry.kind !== "response").length,
    clear: clearLogEntries,
    recording,
    setRecording: setRecordingResponses,
  };
}

/**
 * Catches what never reaches the API client: uncaught exceptions and rejected
 * promises. Mounted once, so a phone can see a crash it can't inspect.
 */
export function useGlobalErrorCapture() {
  React.useEffect(() => {
    const onError = (event: ErrorEvent) => {
      recordLogEntry({
        kind: "crash",
        message: event.message || "Uncaught error",
        details: event.filename ? `${event.filename}:${event.lineno}:${event.colno}` : undefined,
      });
    };
    const onRejection = (event: PromiseRejectionEvent) => {
      const reason = event.reason;
      recordLogEntry({
        kind: "crash",
        message:
          reason instanceof Error
            ? `${reason.name}: ${reason.message}`
            : `Unhandled rejection: ${String(reason)}`,
        details: reason instanceof Error ? reason.stack : undefined,
      });
    };

    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onRejection);
    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onRejection);
    };
  }, []);
}

function CopyButton({ text, label = "Copy" }: { text: string; label?: string }) {
  const [copied, setCopied] = React.useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // Clipboard is blocked in some mobile webviews — fall back to a selection.
      const area = document.createElement("textarea");
      area.value = text;
      area.style.position = "fixed";
      area.style.opacity = "0";
      document.body.append(area);
      area.select();
      try {
        document.execCommand("copy");
      } catch {
        /* nothing more we can do */
      }
      area.remove();
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Button variant="outline" size="sm" onClick={copy}>
      {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
      {copied ? "Copied" : label}
    </Button>
  );
}

function statusTone(entry: LogEntry) {
  if (entry.kind === "crash") return "destructive" as const;
  if (entry.kind === "response") return "success" as const;
  if (entry.status === 0) return "warning" as const;
  if ((entry.status ?? 0) >= 500) return "destructive" as const;
  return "secondary" as const;
}

function EntryCard({ entry }: { entry: LogEntry }) {
  const time = new Date(entry.at).toLocaleTimeString("en-GB");

  return (
    <li className="rounded-xl border">
      <div className="flex flex-wrap items-center gap-2 px-3 pt-3">
        <Badge variant={statusTone(entry)}>
          {entry.kind === "crash" ? "Crash" : (entry.status ?? "—")}
        </Badge>
        {entry.kind === "response" && <span className="text-[11px] text-muted-foreground">recorded</span>}
        {entry.code && (
          <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-[11px]">{entry.code}</code>
        )}
        <span className="ml-auto text-[11px] text-muted-foreground tabular">{time}</span>
      </div>

      {entry.kind !== "crash" && (
        <p className="px-3 pt-1.5 font-mono text-[11px] break-all text-muted-foreground">
          {entry.method} {entry.path}
          {entry.durationMs !== undefined ? ` · ${entry.durationMs}ms` : ""}
        </p>
      )}

      <p className="px-3 pt-1.5 pb-3 text-sm text-pretty select-all">{entry.message}</p>

      {entry.requestId && (
        <p className="border-t px-3 py-2 font-mono text-[11px] break-all text-muted-foreground select-all">
          x-request-id: {entry.requestId}
        </p>
      )}

      {entry.sentBody !== undefined && (
        <details className="border-t">
          <summary className="flex cursor-pointer items-center gap-1.5 px-3 py-2 text-xs text-muted-foreground">
            <ChevronDown className="size-3" />
            What we sent
          </summary>
          <pre className="max-h-52 overflow-auto border-t bg-muted/40 px-3 py-2 font-mono text-[11px] whitespace-pre-wrap select-all">
            {safeJson(entry.sentBody)}
          </pre>
        </details>
      )}

      {entry.responseBody !== undefined && (
        <details className="border-t" open>
          <summary className="flex cursor-pointer items-center gap-1.5 px-3 py-2 text-xs text-muted-foreground">
            <ChevronDown className="size-3" />
            Response
          </summary>
          <pre className="max-h-72 overflow-auto border-t bg-muted/40 px-3 py-2 font-mono text-[11px] whitespace-pre-wrap select-all">
            {safeJson(entry.responseBody)}
          </pre>
        </details>
      )}

      {entry.details !== undefined && entry.details !== null && (
        <details className="border-t" open>
          <summary className="flex cursor-pointer items-center gap-1.5 px-3 py-2 text-xs text-muted-foreground">
            <ChevronDown className="size-3" />
            What the server said
          </summary>
          <pre className="max-h-64 overflow-auto border-t bg-muted/40 px-3 py-2 font-mono text-[11px] whitespace-pre-wrap select-all">
            {typeof entry.details === "string" ? entry.details : safeJson(entry.details)}
          </pre>
        </details>
      )}
    </li>
  );
}

/**
 * The whole failure log, readable and copyable without dev tools. Opened from
 * the account menu.
 */
export function DiagnosticsSheet({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { entries, clear, recording } = useDiagnostics();

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="max-h-[88dvh] gap-0 sm:max-h-[80dvh]">
        <SheetHeader className="pb-3">
          <SheetTitle className="flex items-center gap-2">
            <Stethoscope className="size-4 text-primary" />
            Diagnostics
            {entries.length > 0 && <Badge variant="secondary">{entries.length}</Badge>}
          </SheetTitle>
          <p className="text-sm text-muted-foreground">
            Every failed request and crash this session, newest first. Nothing is stored — it clears
            on reload. Passwords and tokens are never recorded.
          </p>
        </SheetHeader>

        <div className="flex flex-wrap items-center gap-2 px-5 pb-3">
          <CopyButton text={formatLogForSharing(entries)} label="Copy everything" />
          {entries.length > 0 && (
            <Button variant="ghost" size="sm" onClick={clear}>
              <Trash2 className="size-3.5" />
              Clear
            </Button>
          )}
        </div>

        {/* Off by default: it turns the log into a record of your own data. */}
        <div className="mx-5 mb-3 flex items-start gap-3 rounded-xl border p-3">
          <Switch
            id="record-responses"
            checked={recording}
            onCheckedChange={setRecordingResponses}
          />
          <div className="min-w-0">
            <Label htmlFor="record-responses" className="text-sm font-medium">
              Record successful responses
            </Label>
            <p className="mt-0.5 text-xs text-muted-foreground">
              For pinning down a field the app is reading wrongly. Turn it on, revisit the page, and
              the payload appears below. Stays on across reloads; sign-in responses are never
              recorded.
            </p>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-5">
          {entries.length === 0 ? (
            <div className="flex flex-col items-center gap-2 rounded-xl border border-dashed py-10 text-center">
              <Check className="size-5 text-[var(--status-good)]" />
              <p className="text-sm font-medium">Nothing has failed</p>
              <p className="max-w-xs text-sm text-muted-foreground">
                If something goes wrong, come back here and copy the details.
              </p>
            </div>
          ) : (
            <ul className="space-y-2.5">
              {entries.map((entry) => (
                <EntryCard key={entry.id} entry={entry} />
              ))}
            </ul>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

/** A quiet button that only appears once something has actually failed. */
export function DiagnosticsBadge({ onClick, className }: { onClick: () => void; className?: string }) {
  const { count } = useDiagnostics();
  if (!count) return null;

  return (
    <Button
      variant="ghost"
      size="icon-sm"
      onClick={onClick}
      aria-label={`${count} problem${count === 1 ? "" : "s"} recorded — open diagnostics`}
      className={cn("relative text-[var(--warning)]", className)}
    >
      <TriangleAlert className="size-4" />
      <span className="absolute -top-0.5 -right-0.5 grid size-4 place-items-center rounded-full bg-[var(--warning)] text-[9px] font-bold text-black">
        {count > 9 ? "9+" : count}
      </span>
    </Button>
  );
}
